import { ref } from 'vue'
import { apiClient } from '../utils/apiClient.js'

/**
 * Transliteration composable
 * Provides English to Tamil transliteration using the Thamizhi library
 */
export function useTransliterate() {
  const isLoading = ref(false)
  const suggestions = ref([])
  const error = ref(null)

  // Debounce timer
  let debounceTimer = null

  /**
   * Transliterate English text to Tamil
   * @param {string} text - English phonetic text
   * @returns {Promise<{text: string, suggestions: Array}>}
   */
  const transliterate = async (text) => {
    if (!text || !text.trim()) {
      suggestions.value = []
      return { text: '', suggestions: [] }
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/v1/transliterate', { text: text.trim() })
      suggestions.value = response.suggestions || []
      return response
    } catch (err) {
      error.value = err.message || 'Transliteration failed'
      return { text, suggestions: [] }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Transliterate with debouncing (for real-time input)
   * @param {string} text - English phonetic text
   * @param {number} delay - Debounce delay in ms (default: 150)
   * @returns {Promise<{text: string, suggestions: Array}>}
   */
  const transliterateDebounced = (text, delay = 150) => {
    return new Promise((resolve) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      debounceTimer = setTimeout(async () => {
        const result = await transliterate(text)
        resolve(result)
      }, delay)
    })
  }

  /**
   * Cancel any pending debounced transliteration
   */
  const cancelDebounce = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  return {
    isLoading,
    suggestions,
    error,
    transliterate,
    transliterateDebounced,
    cancelDebounce,
  }
}
